import React, { useEffect, useMemo, useState } from 'react';
import {
  ProgressHero,
  StatCard,
  LiveTrackingBanner,
  RouteOptimizationCard,
  ScheduleCard,
  ActionAlertsCard,
} from './components';
import {
  ProgressHero as ProgressHeroType,
  DashStat,
  ScheduleStop,
  ActionAlert,
  RouteStop,
} from './salesRepDashboardTypes';
import { AsyncBoundary } from '../../../components/AsyncBoundary';
import { customersApi, Customer } from '../../../services/customersApi';
import { useSalesGoalsStore } from '../../../store/salesGoalsStore';
import { useSalesActivitiesStore } from '../../../store/salesActivitiesStore';
import styles from './SalesRepDashboard.module.css';

function fmtMoney(n: number): string {
  if (n >= 1000) return `$${(n / 1000).toFixed(1)}K`;
  return `$${Math.round(n).toLocaleString()}`;
}

function daysSince(iso: string | null | undefined): number | null {
  if (!iso) return null;
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
}

const SalesRepDashboard: React.FC = () => {
  const progress = useSalesGoalsStore((s) => s.progress);
  const fetchProgress = useSalesGoalsStore((s) => s.fetchProgress);
  const activities = useSalesActivitiesStore((s) => s.items);
  const fetchActivities = useSalesActivitiesStore((s) => s.fetchList);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    setLoading(true); setError(null);
    Promise.all([
      fetchProgress(),
      fetchActivities({ limit: 100 }),
      customersApi.list({ limit: 200 }).then((r: any) => Array.isArray(r) ? r : (r?.data ?? [])).catch(() => []),
    ])
      .then(([_, __, custs]) => setCustomers(custs as Customer[]))
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load dashboard'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const today0 = useMemo(() => {
    const d = new Date(); d.setHours(0, 0, 0, 0); return d;
  }, []);

  const hero: ProgressHeroType = useMemo(() => {
    const todayActs = activities.filter((a) => new Date(a.occurred_at) >= today0);
    const visitsToday = todayActs.filter((a) => a.kind === 'visit').length;
    const meetingsScheduled = activities.filter((a) => a.follow_up_at && new Date(a.follow_up_at) > new Date()).length;
    const todaySales = progress?.weeks?.[progress.weeks.length - 1]?.revenue ?? 0;
    const target = progress?.goal.target_revenue ?? 0;
    const achieved = progress?.achieved_revenue ?? 0;
    const remaining = Math.max(target - achieved, 0);
    return {
      todayProgress: fmtMoney(todaySales),
      storesVisited: visitsToday,
      appointmentsScheduled: meetingsScheduled,
      weeklyTarget: `${progress?.achieved_revenue_pct ?? 0}%`,
      remaining: `${fmtMoney(remaining)} / ${fmtMoney(target)}`,
      progressPct: progress?.achieved_revenue_pct ?? 0,
    };
  }, [progress, activities, today0]);

  const stats: DashStat[] = useMemo(() => {
    const myRevenue = progress?.achieved_revenue ?? 0;
    const repCustomerIds = new Set(customers.map((c) => c.id));
    const atRisk = customers.filter((c) => {
      const ds = daysSince(c.metrics.last_order_at);
      return ds !== null && ds >= 14 && c.metrics.total_revenue > 0;
    }).length;
    const commission = Math.round(myRevenue * 0.10);
    return [
      { id: 's1', icon: 'sales', label: 'My Sales', value: fmtMoney(myRevenue), color: 'green' },
      { id: 's2', icon: 'customers', label: 'My Customers', value: String(repCustomerIds.size), color: 'blue' },
      { id: 's3', icon: 'risk', label: 'At Risk', value: String(atRisk), color: 'orange' },
      { id: 's4', icon: 'commission', label: 'Commission', value: fmtMoney(commission), color: 'purple' },
    ];
  }, [progress, customers]);

  const schedule: ScheduleStop[] = useMemo(() => {
    return activities
      .filter((a) => {
        if (!a.follow_up_at) return false;
        const d = new Date(a.follow_up_at);
        return d >= today0 && d < new Date(today0.getTime() + 86_400_000);
      })
      .slice(0, 6)
      .map((a) => {
        const t = new Date(a.follow_up_at!);
        return {
          id: String(a.id),
          name: a.customer_name || a.subject,
          type: a.kind === 'visit' ? 'In-person visit' : a.kind === 'call' ? 'Phone Call' : a.kind === 'meeting' ? 'Meeting' : 'Note',
          time: t.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
          status: t < new Date() ? 'completed' : 'upcoming',
        };
      });
  }, [activities, today0]);

  const routeStops: RouteStop[] = useMemo(() => {
    const tomorrow0 = new Date(today0.getTime() + 86_400_000);
    const todaysVisits = activities
      .filter((a) => a.kind === 'visit' && a.follow_up_at)
      .filter((a) => {
        const d = new Date(a.follow_up_at!);
        return d >= today0 && d < tomorrow0;
      })
      .sort((a, b) => new Date(a.follow_up_at!).getTime() - new Date(b.follow_up_at!).getTime());

    let activeAssigned = false;
    return todaysVisits.map((a, idx) => {
      const cust = a.customer_id != null ? customers.find((c) => c.id === a.customer_id) : undefined;
      const done = !!a.completed_at;
      let state: RouteStop['state'] = 'pending';
      if (done) state = 'done';
      else if (!activeAssigned) { state = 'active'; activeAssigned = true; }

      const address = cust
        ? [cust.address_line1, cust.city, cust.state].filter(Boolean).join(', ') || null
        : null;
      const lastOrder = cust?.metrics?.last_order_at
        ? { amount: cust.metrics.total_revenue, date: cust.metrics.last_order_at }
        : null;

      return {
        id: String(a.id),
        sequence: idx + 1,
        name: a.customer_name || cust?.store_name || cust?.full_name || a.subject,
        address,
        timeLabel: new Date(a.follow_up_at!).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
        state,
        lastOrder,
      };
    });
  }, [activities, customers, today0]);

  const alerts: ActionAlert[] = useMemo(() => {
    return customers
      .filter((c) => c.metrics.total_revenue > 0)
      .map((c) => ({ c, days: daysSince(c.metrics.last_order_at) }))
      .filter(({ days }) => days !== null && days >= 7)
      .sort((a, b) => (b.days ?? 0) - (a.days ?? 0))
      .slice(0, 3)
      .map(({ c, days }) => ({
        id: String(c.id),
        customer: c.store_name || c.full_name || c.email,
        description: (days ?? 0) >= 14 ? `Hasn't ordered in ${days} days` : 'Reorder likely soon',
        ctaLabel: (days ?? 0) >= 14 ? 'Schedule Visit →' : 'Create Order →',
        daysSince: `${days} days back`,
      }));
  }, [customers]);

  return (
    <div className={styles.container}>
      <AsyncBoundary
        loading={loading && customers.length === 0 && !progress}
        error={error}
        empty={false}
        onRetry={load}
      >
        <ProgressHero data={hero} />

        <div className={styles.statsGrid}>
          {stats.map((s) => (
            <StatCard key={s.id} stat={s} />
          ))}
        </div>

        <LiveTrackingBanner />
        <RouteOptimizationCard stops={routeStops} />
        <ScheduleCard stops={schedule} />
        <ActionAlertsCard alerts={alerts} />
      </AsyncBoundary>
    </div>
  );
};

export const SalesRepDashboardPage: React.FC = () => {
  return <SalesRepDashboard />;
};

export { SalesRepDashboard };
export default SalesRepDashboard;
